import { router, useFocusEffect } from 'expo-router';
import { useCallback, useRef } from 'react';
import { InteractionManager, Platform } from 'react-native';

import { STORE_LOCATOR_PICKUP_HREF } from '@fhs/store-locator/src/constants';
import { useGeolocation } from 'state/geolocation';
import { isSSG } from 'utils/environment';
import { useAreFlagsReady, useFlag } from 'utils/launchdarkly';

const WEB_ROUTE_PREFETCH_DELAY_MS = 500;

/**
 * Preloads the store locator while the guest is still on home, so the tap lands on a screen
 * that has already mounted and fetched instead of one starting from nothing.
 *
 * Nothing here asks for a position or fetches anything itself. The geolocation provider
 * already loads a fix at app init when permission is granted, and preloading the route mounts
 * the locator, whose own hooks run the restaurant queries.
 */
export function useWarmStoreLocatorFromHome() {
  const { coordinatesAvailable } = useGeolocation();
  const enableStoreLocatorV2 = useFlag('enable-store-locator-v2');
  const areFlagsReady = useAreFlagsReady();

  // Home is never unmounted once it has been visited, so this effect is re-run on every return
  // to it as well as whenever geolocation and flags settle. The latch keeps the preload the
  // single pass it was meant to be.
  const hasPrefetchedRoute = useRef(false);

  // Preloading the route mounts the locator rather than just warming its bundle, so it waits
  // for the things that mount decides with. Coordinates, because mounting without them leaves
  // its queries skipped and its map without a centre. And LaunchDarkly, because only the v2
  // locator is warmed — reading `enable-store-locator-v2` from its fallback would skip it. The
  // URL has to be the one home's pickup button opens, or the tap mounts a new locator beside
  // the warm one.
  //
  // Only while home is on screen: it also sits mounted under any page opened directly, and a
  // preload from there would mount a second locator beside one the guest is already using.
  useFocusEffect(
    useCallback(() => {
      if (
        isSSG ||
        hasPrefetchedRoute.current ||
        !coordinatesAvailable ||
        !areFlagsReady ||
        !enableStoreLocatorV2
      ) {
        return;
      }

      let cancelled = false;
      let timeoutId: ReturnType<typeof setTimeout> | undefined;
      let interactionHandle: ReturnType<typeof InteractionManager.runAfterInteractions> | undefined;

      // Latched on the preload itself, not on scheduling it, so leaving home before the delay
      // runs out leaves it to be retried on the next visit.
      const prefetchRoute = () => {
        if (!cancelled) {
          hasPrefetchedRoute.current = true;
          router.prefetch(STORE_LOCATOR_PICKUP_HREF);
        }
      };

      if (Platform.OS === 'web') {
        timeoutId = setTimeout(prefetchRoute, WEB_ROUTE_PREFETCH_DELAY_MS);
      } else {
        interactionHandle = InteractionManager.runAfterInteractions(prefetchRoute);
      }

      return () => {
        cancelled = true;
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        interactionHandle?.cancel();
      };
    }, [areFlagsReady, coordinatesAvailable, enableStoreLocatorV2])
  );
}
